import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Tag, Select, Button, Input, Pagination, Checkbox, message, Empty, Modal, Tooltip, Upload, DatePicker } from "antd";
import dayjs from "dayjs";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import "./Inbox.css";
import {
  getAllTickets,
  updateTicket,
  bulkUpdateTickets,
  getAcountOwnerList,
  replyToTicket,
  getTicketThread,
  getTicketFilterOptions,
  getTicketsNeedingAttention,
  syncTicketInbox,
  getUserById,
  getUserDirectory,
  composeTicket,
} from "../../services/ApiService";
import {
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  WarningOutlined,
  SyncOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CoffeeOutlined,
  PlusOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { decryptObject } from "../GlobalFunction";
import { notifyTicketCountChanged } from "../../utils/ticketEvents";

// How often to re-fetch the ticket list in the background, so a new
// ticket (created by the 2-minute scheduler poll -- scheduler/
// ticket_inbox_job.py -- or by someone else) shows up without the user
// having to refresh the page. Matches the Sidebar badge's own polling
// cadence.
const TICKET_LIST_POLL_MS = 15000;
// How long the manual "Refresh" button stays disabled after a sync
// completes, so repeated clicks can't spam the Graph API.
const SYNC_COOLDOWN_MS = 10000;

const REPLY_EDITOR_MODULES = {
  toolbar: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]],
};

const URGENCY_COLOR = { high: "red", medium: "gold", low: "blue" };
// ISO classification -- computed server-side (see
// services/ticket_service.py::_classify_outgoing_email), internal
// tracking only, shown here for audit visibility, never part of the
// sent email itself.
const CLASSIFICATION_COLOR = { Public: "green", Internal: "blue", Restricted: "red" };
const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "closed", label: "Closed" },
];
// Filter-bar-only variant -- clearing the filter already defaults to
// Open + In Progress (see services/ticket_service.py::get_all_tickets);
// "All Statuses" is the explicit way to see every ticket regardless of
// status, confirmed with the user, 2026-10-19. Kept separate from
// STATUS_OPTIONS so "All" never leaks into the bulk/detail status-SET
// dropdowns, where it isn't a real status.
const STATUS_FILTER_OPTIONS = [...STATUS_OPTIONS, { value: "all", label: "All Statuses" }];
// Mirrors services/ticket_analysis_service.py::TICKET_CATEGORIES -- kept
// in sync manually, same as STATUS_OPTIONS above.
const CATEGORY_OPTIONS = [
  "Payroll Query",
  "Onboarding Query",
  "Offboarding/Termination Query",
  "Invoice/Billing Query",
  "Document Request",
  "Compliance/Legal Query",
  "Complaint",
  "General Query",
  "Other",
].map((c) => ({ value: c, label: c }));
const URGENCY_OPTIONS = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "urgency", label: "Urgency" },
  { value: "longest_open", label: "Longest open" },
];
// Top-level list layout -- confirmed with the user, 2026-11-02. "Group by
// Category" has been removed outright (never reinstated). "assign_to"
// (default) is today's owner-sectioned view: Unassigned / My Tickets /
// everyone else, each section a flat newest-first list. "date" is a
// single flat newest-first list across every owner, with no sections at
// all -- just the plain chronological inbox the team actually wants,
// latest on top.
const GROUP_MODE_OPTIONS = [
  { value: "assign_to", label: "Group by Assign To" },
  { value: "date", label: "Group by Date" },
];
const PAGE_SIZE = 20;

// Mirrors services/ticket_service.py::STALE_REMINDER_HOURS /
// STALE_ESCALATION_HOURS -- kept in sync manually, same as the other
// mirrored constants in this file. Purely for the list's age badge; the
// actual reminder/escalation notifications are sent server-side.
const AGE_WARNING_HOURS = 24;
const AGE_CRITICAL_HOURS = 48;

const getAgeBadge = (receivedAt, status) => {
  if (!receivedAt || status !== "open") return null;
  const hours = (Date.now() - new Date(receivedAt).getTime()) / (1000 * 60 * 60);
  if (hours >= AGE_CRITICAL_HOURS) return { label: `Open ${Math.floor(hours / 24)}d+`, color: "red" };
  if (hours >= AGE_WARNING_HOURS) return { label: `Open ${Math.floor(hours)}h+`, color: "gold" };
  return null;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const formatDateOnly = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString();
};

// Quill's "empty" value is still markup (e.g. "<p><br></p>"), so a plain
// .trim() check on the HTML string never reads as empty -- strip tags
// first and check the leftover text.
const isReplyEmpty = (html) => !html || html.replace(/<[^>]*>/g, "").trim().length === 0;

const Inbox = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  // Mirrors `selected`'s id without needing it in the polling effect's
  // dependency array (which would otherwise reset the interval's timing
  // every time a different ticket is clicked) -- lets the background
  // poll always read the CURRENT selection, confirmed with the user,
  // 2026-10-28: the open thread panel was never refreshed in the
  // background (only the list was), so new messages on an open ticket
  // stayed invisible until it was reselected or the page was reloaded.
  const selectedIdRef = useRef(null);
  useEffect(() => {
    selectedIdRef.current = selected?.id || null;
  }, [selected]);
  const [assigneeInput, setAssigneeInput] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Manual "Refresh" button -- forces an immediate mailbox poll instead
  // of waiting for the 2-minute scheduled job, confirmed with the user,
  // 2026-10-28. `syncCooldown` keeps the button disabled for a few
  // seconds after a sync completes (rather than only while in flight),
  // so someone clicking it repeatedly can't spam /process-ticket-inbox
  // and risk tripping Graph API throttling -- confirmed with the user,
  // 2026-10-28.
  const [syncing, setSyncing] = useState(false);
  const [syncCooldown, setSyncCooldown] = useState(false);
  const [accountOwnerOptions, setAccountOwnerOptions] = useState([]);
  const [thread, setThread] = useState([]);
  const [replyBody, setReplyBody] = useState("");
  // Own rich-text signature, seeded into the composer on each ticket
  // open (see handleSelect) -- demo feedback item 2, confirmed with the
  // user, 2026-11-03.
  const [mySignature, setMySignature] = useState("");
  // Internal users (name + email) for the To/Cc autosuggest -- demo
  // feedback item 4, confirmed with the user, 2026-11-03: internal
  // colleagues to CC, not client contacts.
  const [userDirectory, setUserDirectory] = useState([]);
  // "Compose New Email" -- starts a brand-new outbound ticket (demo
  // feedback, confirmed with the user, 2026-11-03), separate from the
  // reply composer's state since it has no ticket context yet.
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState([]);
  const [composeCc, setComposeCc] = useState([]);
  const [composeBcc, setComposeBcc] = useState([]);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [composing, setComposing] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  // Editable To/Cc on the reply composer -- From stays fixed (the shared
  // mailbox, see replyFromEmail below), confirmed with the user,
  // 2026-10-19. Each a list of email strings, pre-filled with the usual
  // default (original sender / assigned owner) when a ticket is opened,
  // then freely add/remove-able before sending.
  const [replyTo, setReplyTo] = useState([]);
  const [replyCc, setReplyCc] = useState([]);
  // No default like To/Cc -- an omitted Bcc just means no Bcc at all.
  const [replyBcc, setReplyBcc] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  const [replyFromEmail, setReplyFromEmail] = useState("");
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    status: null,
    category: null,
    urgency: null,
    assigned_owner: null,
    sender_email: "",
    client_id: null,
    client_location: null,
    date_from: "",
    date_to: "",
    search: "",
    sort_by: "newest",
  });
  const [searchInput, setSearchInput] = useState("");
  const [senderEmailInput, setSenderEmailInput] = useState("");
  const [filterOptions, setFilterOptions] = useState({ clients: [], client_locations: [], assignable_owners: [] });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [bulkAssignee, setBulkAssignee] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  // "View" button on a row opens the same detail content in a modal,
  // confirmed with the user, 2026-10-19 -- alongside the right-side panel
  // (not instead of it), since both stay in sync off the same `selected`
  // ticket (View also calls handleSelect, same as clicking the row does).
  const [viewModalOpen, setViewModalOpen] = useState(false);
  // Which owner groups are collapsed in the ticket list -- default is
  // empty (everything expanded), since collapsing is something the user
  // does to reduce clutter, not something they start from. Keyed by
  // employee_id, or "unassigned" for the Unassigned group.
  const [collapsedOwners, setCollapsedOwners] = useState(new Set());
  // "assign_to" (default) groups by owner; "date" is one flat
  // newest-first list across everyone -- confirmed with the user,
  // 2026-11-02.
  const [groupMode, setGroupMode] = useState("assign_to");
  // "Needs Attention" banner -- every open, unassigned, high-urgency
  // ticket the user can see, confirmed with the user, 2026-10-26: the
  // escalation ladder never covers unassigned tickets (no assignee to
  // walk a hierarchy from) and the default sort just lets them sink as
  // new tickets arrive, so this surfaces them regardless of sort/filter.
  // Naturally empty for an individual contributor (unassigned tickets
  // are only visible to leads/managers in the first place), so no extra
  // role check is needed here.
  const [needingAttention, setNeedingAttention] = useState([]);

  const sessionData = decryptObject(sessionStorage.getItem("sessioninfo"));
  const currentEmployeeId = sessionData?.employee_id;
  // userType 1 = Admin (see Routing.js) -- explicit override so Admin can
  // see/use the reply composer on any ticket regardless of assignment,
  // matching the blanket Admin bypass used elsewhere in this app's
  // permission system. The backend grants the same exception.
  // userType's stored type is inconsistent across the app (sometimes a
  // string, sometimes a number -- RouteGuard.js coerces with Number() for
  // the same reason), so compare numerically, not with strict ===.
  const isAdmin = Number(sessionData?.userType) === 1;

  useEffect(() => {
    fetchAccountOwners();
    fetchFilterOptions();
    fetchMySignature();
    fetchUserDirectory();
  }, []);

  const fetchMySignature = async () => {
    if (!sessionData?.id) return;
    try {
      const res = await getUserById(sessionData.id);
      if (res?.responseCode === 200) {
        setMySignature(res.data?.signature || "");
      }
    } catch (error) {
      console.error("Failed to fetch signature:", error);
    }
  };

  const fetchUserDirectory = async () => {
    try {
      const res = await getUserDirectory();
      if (res?.responseCode === 200) {
        setUserDirectory(
          (res.users || []).map((u) => ({
            value: u.email_id,
            label: `${u.name} <${u.email_id}>`,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch user directory:", error);
    }
  };

  const fetchNeedingAttention = async () => {
    try {
      const res = await getTicketsNeedingAttention();
      if (res?.responseCode === 200) {
        setNeedingAttention(res.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch tickets needing attention:", error);
    }
  };

  // Independent of filters/page -- the banner always reflects the full
  // unfiltered set, same poll cadence as the main ticket list.
  useEffect(() => {
    fetchNeedingAttention();
    const interval = setInterval(fetchNeedingAttention, TICKET_LIST_POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const toggleCollapsed = (ownerId) => {
    setCollapsedOwners((prev) => {
      const next = new Set(prev);
      if (next.has(ownerId)) next.delete(ownerId);
      else next.add(ownerId);
      return next;
    });
  };

  const fetchFilterOptions = async () => {
    try {
      const res = await getTicketFilterOptions();
      if (res?.responseCode === 200) {
        setFilterOptions({
          clients: res.clients || [],
          client_locations: res.client_locations || [],
          assignable_owners: res.assignable_owners || [],
        });
      }
    } catch (error) {
      console.error("Failed to fetch ticket filter options:", error);
    }
  };

  // Re-runs (and resets the poll interval) whenever a filter or the page
  // changes -- harmless, just means the next poll tick is measured from
  // the most recent change rather than mount time. Each tick also
  // refreshes the currently open ticket's thread (via selectedIdRef, see
  // above) so a reply/internal note that lands on it while it's open
  // shows up without having to reselect the ticket.
  useEffect(() => {
    fetchTickets();
    const interval = setInterval(() => {
      fetchTickets();
      if (selectedIdRef.current) fetchThread(selectedIdRef.current);
    }, TICKET_LIST_POLL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, currentPage]);

  // Opens a ticket straight from a notification's "/inbox?ticket_id=NNN"
  // link_url (see services/ticket_service.py's Notification creation
  // sites) -- confirmed with the user, 2026-11-02. A direct single-ticket
  // lookup (not the paginated/filtered list) since the ticket might not
  // be on the current page or even match the current filters. Clears the
  // query param once opened so a later manual refresh of /inbox doesn't
  // keep reopening it.
  useEffect(() => {
    const ticketId = searchParams.get("ticket_id");
    if (!ticketId) return;
    (async () => {
      try {
        const res = await getAllTickets({ ticket_id: ticketId }, 1, 1);
        if (res.responseCode === 200 && res.data?.[0]) {
          handleSelect(res.data[0]);
        } else {
          message.error("That ticket couldn't be found.");
        }
      } catch (error) {
        console.error("Failed to open ticket from notification:", error);
      } finally {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("ticket_id");
          return next;
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Same source the onboarding form's Account Owner field (id 69) uses --
  // real Users flagged account_owner=1, keyed by employee_id so this
  // matches canonically-stored assignments rather than the free-text
  // display-name values some older data has (see MasterDatabase's filter
  // dropdown, which deliberately uses name-as-value for a different,
  // historical-data reason -- not applicable here since this is a new
  // field going forward).
  const fetchAccountOwners = async () => {
    try {
      const res = await getAcountOwnerList();
      if (res?.responseCode === 200) {
        const options = (res?.res || []).map((owner) => ({
          label: owner.name,
          value: owner.id,
        }));
        setAccountOwnerOptions(options);
      }
    } catch (error) {
      console.error("Failed to fetch account owners:", error);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await getAllTickets(filters, currentPage, PAGE_SIZE);
      if (res.responseCode === 200) {
        setTickets(res.data || []);
        setTotalRecords(res.total_records || 0);
        setReplyFromEmail(res.reply_from_email || "");
      }
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      const res = await syncTicketInbox();
      if (res?.responseCode === 200) {
        const created = res.tickets_created || 0;
        message.success(
          created > 0
            ? `Synced -- ${created} new ticket${created === 1 ? "" : "s"} fetched.`
            : "Synced -- no new emails since the last check."
        );
        notifyTicketCountChanged();
        fetchTickets();
        fetchNeedingAttention();
      } else {
        message.error(res?.responseMessage || "Failed to sync the inbox.");
      }
    } catch (error) {
      console.error("Failed to sync inbox:", error);
      message.error("Failed to sync the inbox.");
    } finally {
      setSyncing(false);
      setSyncCooldown(true);
      setTimeout(() => setSyncCooldown(false), SYNC_COOLDOWN_MS);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setSelectedIds([]);
  };

  // The "Needs Attention" strip doesn't render its own ticket rows (that
  // was the space-hungry part) -- clicking it just filters the REAL list
  // down to exactly those tickets instead, confirmed with the user,
  // 2026-10-27.
  const handleShowNeedingAttention = () => {
    setFilters((prev) => ({ ...prev, assigned_owner: "unassigned", urgency: "high" }));
    setCurrentPage(1);
    setSelectedIds([]);
  };

  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, search: value.trim() }));
    setCurrentPage(1);
    setSelectedIds([]);
  };

  const handleSenderEmailSearch = (value) => {
    setFilters((prev) => ({ ...prev, sender_email: value.trim() }));
    setCurrentPage(1);
    setSelectedIds([]);
  };

  // dates is [dayjs, dayjs] | null from the RangePicker -- format with
  // seconds so an explicitly-picked time (showTime) survives the round
  // trip to the backend, which otherwise treats a bare date's "to" as
  // end-of-day by default (see _parse_ticket_date_filter).
  const handleDateRangeChange = (dates) => {
    setFilters((prev) => ({
      ...prev,
      date_from: dates?.[0] ? dates[0].format("YYYY-MM-DD HH:mm:ss") : "",
      date_to: dates?.[1] ? dates[1].format("YYYY-MM-DD HH:mm:ss") : "",
    }));
    setCurrentPage(1);
    setSelectedIds([]);
  };

  const handleClearFilters = () => {
    setFilters({
      status: null,
      category: null,
      urgency: null,
      assigned_owner: null,
      sender_email: "",
      client_id: null,
      client_location: null,
      date_from: "",
      date_to: "",
      search: "",
      sort_by: "newest",
    });
    setSearchInput("");
    setSenderEmailInput("");
    setCurrentPage(1);
    setSelectedIds([]);
  };

  const handleSelect = (ticket) => {
    setSelected(ticket);
    setAssigneeInput(ticket.assigned_owner || null);
    // Seeds the composer with the signature exactly once per ticket-open
    // (handleSelect only fires on a row click/ticket switch, never on an
    // editor keystroke) -- never re-inserted/duplicated while typing or
    // when switching back to a ticket already open before.
    setReplyBody(mySignature ? `<p><br></p>${mySignature}` : "");
    setShowReplyForm(false);
    setReplySubject(
      ticket.subject?.toLowerCase().startsWith("re:") ? ticket.subject : `RE: ${ticket.subject || ""}`
    );
    setReplyTo(ticket.sender_email ? [ticket.sender_email] : []);
    setReplyCc(ticket.assigned_owner_email ? [ticket.assigned_owner_email] : []);
    setReplyBcc([]);
    setReplyAttachments([]);
    fetchThread(ticket.id);
    // Opening it is what the backend treats as "viewed" (get_ticket_thread
    // sets last_viewed_at) -- reflect that immediately in the list instead
    // of waiting for the next poll, same as any other local-update pattern
    // in this file. has_unread_reply is cleared the same way, same reason.
    applyLocalUpdate(ticket.id, { is_new: false, has_unread_reply: false });
    // The Sidebar badge now counts unread tickets (confirmed with the
    // user, 2026-10-29), so reading one changes it -- nudge it to
    // refetch right away instead of waiting up to 15s for its own poll.
    // Only worth it if this ticket was actually unread; most clicks are
    // on a ticket someone's already read.
    if (ticket.is_new) notifyTicketCountChanged();
  };

  const fetchThread = async (ticketId) => {
    try {
      const res = await getTicketThread(ticketId);
      if (res.responseCode === 200) {
        setThread(res.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch thread:", error);
    }
  };

  const handleSendReply = async () => {
    if (!selected || isReplyEmpty(replyBody)) return;
    if (replyTo.length === 0) {
      message.error("At least one To recipient is required.");
      return;
    }
    try {
      setSendingReply(true);
      const res = await replyToTicket(
        selected.id, replyBody, replySubject.trim(), replyTo, replyCc, replyBcc, replyAttachments
      );
      if (res.responseCode === 200) {
        if (res.dry_run) {
          message.warning("Dry run only -- no email was actually sent.");
        } else {
          message.success("Reply sent");
          applyLocalUpdate(selected.id, { status: selected.status === "open" ? "in_progress" : selected.status });
          notifyTicketCountChanged();
          setShowReplyForm(false);
        }
        setReplyBody("");
        setReplyAttachments([]);
        fetchThread(selected.id);
      } else {
        message.error(res.responseMessage || "Failed to send reply");
      }
    } catch (error) {
      message.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  const openCompose = () => {
    setComposeTo([]);
    setComposeCc([]);
    setComposeBcc([]);
    setComposeSubject("");
    setComposeBody(mySignature ? `<p><br></p>${mySignature}` : "");
    setComposeAttachments([]);
    setComposeOpen(true);
  };

  const handleComposeSend = async () => {
    if (composeTo.length === 0) {
      message.error("At least one To recipient is required.");
      return;
    }
    if (!composeSubject.trim() || isReplyEmpty(composeBody)) {
      message.error("Subject and body are required.");
      return;
    }
    try {
      setComposing(true);
      const res = await composeTicket(
        composeTo, composeCc, composeBcc, composeSubject.trim(), composeBody, composeAttachments
      );
      if (res.responseCode === 200) {
        message[res.dry_run ? "warning" : "success"](
          res.dry_run ? "Dry run only -- no email was actually sent." : "Ticket created and sent"
        );
        setComposeOpen(false);
        fetchTickets();
      } else {
        message.error(res.responseMessage || "Failed to send");
      }
    } catch (error) {
      message.error("Failed to send");
    } finally {
      setComposing(false);
    }
  };

  // Mirrors services/ticket_service.py::get_all_tickets' status-filter
  // logic -- whether a ticket with this status would actually be
  // included under the list's CURRENT status filter (not just "did it
  // used to be in the list"). Used to decide whether closing/reopening
  // the currently-open ticket should also clear it out of the detail
  // panel, confirmed real bug, 2026-10-19: closing a ticket correctly
  // dropped it from the list (next poll), but the detail panel kept
  // showing its now-stale content indefinitely since `selected` is
  // independent state the list refresh never touches.
  const isStatusVisibleUnderFilter = (status) => {
    if (filters.status === "all") return true;
    if (filters.status) return filters.status.split(",").includes(status);
    return status === "open" || status === "in_progress";
  };

  const applyLocalUpdate = (ticketId, changes) => {
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, ...changes } : t))
    );
    setSelected((prev) => (prev && prev.id === ticketId ? { ...prev, ...changes } : prev));

    if ("status" in changes && !isStatusVisibleUnderFilter(changes.status)) {
      setTickets((prev) => prev.filter((t) => t.id !== ticketId));
      setSelected((prev) => {
        if (prev && prev.id === ticketId) {
          setThread([]);
          setShowReplyForm(false);
          setViewModalOpen(false);
          return null;
        }
        return prev;
      });
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    try {
      const res = await updateTicket(selected.id, { status: newStatus });
      if (res.responseCode === 200) {
        applyLocalUpdate(selected.id, { status: newStatus });
        notifyTicketCountChanged();
        message.success("Status updated");
      } else {
        message.error(res.responseMessage || "Failed to update status");
      }
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const handleAssignSave = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const res = await updateTicket(selected.id, { assigned_owner: assigneeInput });
      if (res.responseCode === 200) {
        applyLocalUpdate(selected.id, { assigned_owner: assigneeInput || null });
        message.success("Assignee updated");
      } else {
        message.error(res.responseMessage || "Failed to update assignee");
      }
    } catch (error) {
      message.error("Failed to update assignee");
    } finally {
      setSaving(false);
    }
  };

  const toggleRowSelection = (ticketId, e) => {
    e.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(ticketId) ? prev.filter((id) => id !== ticketId) : [...prev, ticketId]
    );
  };

  const toggleSelectAllOnPage = (e) => {
    if (e.target.checked) {
      setSelectedIds(tickets.map((t) => t.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleBulkApply = async () => {
    if (selectedIds.length === 0 || (!bulkStatus && !bulkAssignee)) return;
    try {
      setBulkApplying(true);
      const body = {};
      if (bulkStatus) body.status = bulkStatus;
      if (bulkAssignee) body.assigned_owner = bulkAssignee;
      const res = await bulkUpdateTickets(selectedIds, body);
      if (res.responseCode === 200) {
        message.success(res.responseMessage);
        // Same stale-detail-panel fix as applyLocalUpdate, for the bulk
        // path -- a bulk status change can also close/reopen the
        // currently-open ticket out from under the detail panel.
        if (bulkStatus && selected && selectedIds.includes(selected.id) && !isStatusVisibleUnderFilter(bulkStatus)) {
          setSelected(null);
          setThread([]);
          setShowReplyForm(false);
          setViewModalOpen(false);
        }
        setSelectedIds([]);
        setBulkStatus(null);
        setBulkAssignee(null);
        notifyTicketCountChanged();
        fetchTickets();
      } else {
        message.error(res.responseMessage || "Bulk update failed");
      }
    } catch (error) {
      message.error("Bulk update failed");
    } finally {
      setBulkApplying(false);
    }
  };

  // Shared between the right-side detail panel and the "View" modal so
  // both always show identical content for `selected` -- confirmed with
  // the user, 2026-10-19: View opens a modal with the same details
  // ALONGSIDE the right panel, not instead of it.
  const renderDetailCard = () => (
    <>
      <div className="inbox-detail-header">
        <div className="inbox-detail-header-right">
          <span className="inbox-row-time">{formatDateTime(selected.received_at)}</span>
          <div className="inbox-detail-nav">
            <Tooltip title="Previous email">
              <Button
                size="small"
                type="text"
                icon={<LeftOutlined />}
                disabled={!hasPrevTicket}
                onClick={() => handleNavigateTicket(-1)}
              />
            </Tooltip>
            <Tooltip title="Next email">
              <Button
                size="small"
                type="text"
                icon={<RightOutlined />}
                disabled={!hasNextTicket}
                onClick={() => handleNavigateTicket(1)}
              />
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="inbox-detail-meta">
        <span><strong>From:</strong> {selected.sender_name ? `${selected.sender_name} <${selected.sender_email}>` : selected.sender_email}</span>
        {/* Only present for tickets ingested after this field was added --
            older tickets never had their recipients captured, so this line
            just doesn't render for them. */}
        {selected.to_recipients && (
          <span><strong>To:</strong> {selected.to_recipients}</span>
        )}
        {/* Almost never present -- Graph only exposes Bcc on the
            sender's own Sent Items copy, not a recipient's Inbox copy,
            so this is only non-empty in rare edge cases. */}
        {selected.bcc_recipients && (
          <span><strong>Bcc:</strong> {selected.bcc_recipients}</span>
        )}
        <span><strong>Client:</strong> {selected.client_name || "Unrecognized sender"}</span>
        <span><strong>Subject:</strong> {selected.subject}</span>
      </div>

      <div className="inbox-detail-tags">
        {selected.urgency && <Tag color={URGENCY_COLOR[selected.urgency] || "default"}>{selected.urgency} urgency</Tag>}
        {selected.category && <Tag>{selected.category}</Tag>}
        {selected.has_unread_reply && (
          <Tag color="blue" icon={<MessageOutlined />}>
            New reply
          </Tag>
        )}
        {selected.replied_via_app && (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Replied via App
          </Tag>
        )}
        {selected.assigned_owner && selected.assigned_owner_away && (
          <Tag color="gold" icon={<CoffeeOutlined />}>
            {selected.assigned_owner_name || selected.assigned_owner} is away
            {selected.assigned_owner_away_until
              ? ` until ${formatDateOnly(selected.assigned_owner_away_until)}`
              : ""}
          </Tag>
        )}
      </div>

      <div className="inbox-detail-section">
        <div className="inbox-detail-section-label">AI Summary</div>
        <p>{selected.summary || "No summary available."}</p>
      </div>

      <div className="inbox-detail-controls">
        <div className="inbox-detail-control">
          <label>Status</label>
          <Select
            value={selected.status}
            options={STATUS_OPTIONS}
            style={{ width: "100%" }}
            onChange={handleStatusChange}
          />
        </div>
        <div className="inbox-detail-control">
          <label>Assigned to</label>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Select
              value={assigneeInput}
              onChange={(value) => setAssigneeInput(value)}
              options={accountOwnerOptions}
              placeholder="Select an account owner"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
            />
            <Button type="primary" loading={saving} onClick={handleAssignSave}>
              Save
            </Button>
          </div>
        </div>
      </div>

      {selected.assigned_owner && (selected.assigned_owner === currentEmployeeId || isAdmin) ? (
        <div className="inbox-detail-section">
          <Button onClick={() => setShowReplyForm((v) => !v)}>
            {showReplyForm ? "Cancel reply" : "Reply"}
          </Button>

          {showReplyForm && (
            <div className="inbox-reply-composer">
              <div className="inbox-reply-headers">
                <div><span>From</span>{replyFromEmail || "—"}</div>
                <div>
                  <span>To</span>
                  <Select
                    mode="tags"
                    value={replyTo}
                    onChange={(values) => setReplyTo(values.filter((v) => v.includes("@")))}
                    placeholder="Add a recipient email"
                    tokenSeparators={[",", " "]}
                    options={userDirectory}
                    filterOption={(input, option) =>
                      (option?.label || "").toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ flex: 1 }}
                  />
                </div>
                <div>
                  <span>Cc</span>
                  <Select
                    mode="tags"
                    value={replyCc}
                    onChange={(values) => setReplyCc(values.filter((v) => v.includes("@")))}
                    placeholder="Add a Cc email (optional)"
                    tokenSeparators={[",", " "]}
                    options={userDirectory}
                    filterOption={(input, option) =>
                      (option?.label || "").toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ flex: 1 }}
                  />
                </div>
                <div>
                  <span>Bcc</span>
                  <Select
                    mode="tags"
                    value={replyBcc}
                    onChange={(values) => setReplyBcc(values.filter((v) => v.includes("@")))}
                    placeholder="Add a Bcc email (optional)"
                    tokenSeparators={[",", " "]}
                    options={userDirectory}
                    filterOption={(input, option) =>
                      (option?.label || "").toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ flex: 1 }}
                  />
                </div>
                <div>
                  <span>Subject</span>
                  <Input
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    placeholder="Subject"
                  />
                </div>
              </div>
              <div className="inbox-reply-editor" style={{ marginTop: "0.5rem" }}>
                <ReactQuill
                  theme="snow"
                  value={replyBody}
                  onChange={setReplyBody}
                  modules={REPLY_EDITOR_MODULES}
                  placeholder={`Reply to ${selected.sender_email}...`}
                />
              </div>
              <Upload
                multiple
                fileList={replyAttachments.map((f, i) => ({ uid: String(i), name: f.name, status: "done" }))}
                beforeUpload={(file) => {
                  setReplyAttachments((prev) => [...prev, file]);
                  return false;
                }}
                onRemove={(fileItem) =>
                  setReplyAttachments((prev) => prev.filter((_, i) => String(i) !== fileItem.uid))
                }
                style={{ marginTop: "0.5rem" }}
              >
                <Button icon={<UploadOutlined />}>Attach files</Button>
              </Upload>
              <Button
                type="primary"
                loading={sendingReply}
                disabled={isReplyEmpty(replyBody)}
                onClick={handleSendReply}
                style={{ marginTop: "0.5rem" }}
              >
                Send reply
              </Button>
            </div>
          )}
        </div>
      ) : (
        <p className="inbox-no-replies">
          {selected.assigned_owner
            ? "Only the account owner this ticket is assigned to can reply."
            : "Assign this ticket to an account owner before replying."}
        </p>
      )}

      <div className="inbox-detail-section">
        <div className="inbox-detail-section-label">Thread</div>
        {thread.length === 0 ? (
          <p className="inbox-no-replies">Loading thread...</p>
        ) : (
          <div className="inbox-reply-list">
            {thread.map((entry, idx) => (
              <div
                key={idx}
                className={`inbox-thread-item ${entry.type === "outbound" ? "inbox-thread-outbound" : "inbox-thread-inbound"} ${entry.type === "outbound" && !entry.success ? "inbox-reply-failed" : ""}`}
              >
                <div className="inbox-reply-meta">
                  <span>
                    {entry.type === "outbound"
                      ? entry.sender_employee_id
                      : (entry.sender_name || entry.sender_email)}
                    <Tag
                      color={entry.type === "outbound" ? "blue" : "default"}
                      style={{ marginLeft: "0.4rem" }}
                    >
                      {entry.type === "outbound" ? "Sent" : "Received"}
                    </Tag>
                    {entry.type === "outbound" && entry.dry_run && (
                      <Tag color="orange">dry run · not sent</Tag>
                    )}
                    {entry.type === "outbound" && entry.classification && (
                      <Tag color={CLASSIFICATION_COLOR[entry.classification] || "default"}>
                        {entry.classification}
                      </Tag>
                    )}
                  </span>
                  <span>{formatDateTime(entry.timestamp)}</span>
                </div>
                {entry.subject && <p style={{ fontWeight: 600, marginBottom: "0.1rem" }}>{entry.subject}</p>}
                {entry.type === "outbound" ? (
                  <div className="inbox-reply-body-html" dangerouslySetInnerHTML={{ __html: entry.body }} />
                ) : (
                  <p className="inbox-thread-plain-body">{entry.body || "(no content)"}</p>
                )}
                {entry.attachments?.length > 0 && (
                  <div className="inbox-thread-attachments">
                    {entry.attachments.map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noreferrer">
                        <PaperClipOutlined /> {a.filename}
                      </a>
                    ))}
                  </div>
                )}
                {entry.type === "outbound" && !entry.success && (
                  <span className="inbox-reply-error">Failed to send: {entry.error_message}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  // A ticket row -- shared between the flat list (individual contributor,
  // no one reporting to them) and every per-owner group in the manager/
  // admin grouped view, so the row itself looks identical either way.
  // Cheap membership check for the row-level flag below -- recomputed
  // each render from needingAttention state, small enough not to need
  // memoizing.
  const needingAttentionIds = new Set(needingAttention.map((t) => t.id));

  const renderTicketRow = (ticket) => {
    const ageBadge = getAgeBadge(ticket.received_at, ticket.status);
    const needsAttention = needingAttentionIds.has(ticket.id);
    return (
      <div
        key={ticket.id}
        className={`inbox-row ${selected?.id === ticket.id ? "inbox-row-active" : ""} ${ticket.is_new ? "inbox-row-unread" : ""} ${needsAttention ? "inbox-row-needs-attention" : ""}`}
        onClick={() => handleSelect(ticket)}
      >
        <div className="inbox-row-top">
          <div className="inbox-row-title-group">
            <Checkbox
              checked={selectedIds.includes(ticket.id)}
              onClick={(e) => toggleRowSelection(ticket.id, e)}
              onChange={() => {}}
            />
            {ticket.is_new && <span className="inbox-unread-dot" />}
            {needsAttention && <WarningOutlined className="inbox-row-attention-icon" />}
            <span className="inbox-row-title">{ticket.title || ticket.subject || "Untitled ticket"}</span>
            {ticket.has_attachments && (
              <Tooltip title="Has attachment(s)">
                <PaperClipOutlined className="inbox-row-attachment-icon" />
              </Tooltip>
            )}
            {ticket.reply_count > 0 && (
              <Tooltip title={`${ticket.reply_count} repl${ticket.reply_count === 1 ? "y" : "ies"} on this ticket`}>
                <span className="inbox-row-reply-count">
                  <MessageOutlined /> {ticket.reply_count}
                </span>
              </Tooltip>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
            <span className="inbox-row-time">{formatDateTime(ticket.received_at || ticket.created_date)}</span>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(ticket);
                setViewModalOpen(true);
              }}
            >
              View
            </Button>
          </div>
        </div>
        <div className="inbox-row-sender">
          {ticket.sender_name ? `${ticket.sender_name} · ` : ""}{ticket.sender_email}
          {ticket.client_name ? ` · ${ticket.client_name}` : ""}
        </div>
        <div className="inbox-row-tags">
          {ticket.urgency && (
            <Tag color={URGENCY_COLOR[ticket.urgency] || "default"}>{ticket.urgency}</Tag>
          )}
          {ticket.category && <Tag>{ticket.category}</Tag>}
          <Tag color={ticket.status === "closed" ? "green" : ticket.status === "in_progress" ? "processing" : "default"}>
            {STATUS_OPTIONS.find((s) => s.value === ticket.status)?.label || ticket.status}
          </Tag>
          {ticket.assigned_owner ? (
            <Tag color="purple">
              {ticket.assigned_owner_name ||
                accountOwnerOptions.find((o) => o.value === ticket.assigned_owner)?.label ||
                ticket.assigned_owner}
            </Tag>
          ) : (
            <Tag color="default">Unassigned</Tag>
          )}
          {ticket.assigned_owner && ticket.assigned_owner_away && (
            <Tooltip
              title={
                ticket.assigned_owner_away_until
                  ? `Expected back ${formatDateOnly(ticket.assigned_owner_away_until)} -- consider picking this up or reassigning it`
                  : "No return date set -- consider picking this up or reassigning it"
              }
            >
              <Tag color="gold" icon={<CoffeeOutlined />}>
                Away{ticket.assigned_owner_away_until
                  ? ` until ${formatDateOnly(ticket.assigned_owner_away_until)}`
                  : ""}
              </Tag>
            </Tooltip>
          )}
          {ageBadge && <Tag color={ageBadge.color}>{ageBadge.label}</Tag>}
          {ticket.has_unread_reply && (
            <Tag color="blue" icon={<MessageOutlined />}>
              New reply
            </Tag>
          )}
          {ticket.replied_via_app && (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Replied via App
            </Tag>
          )}
        </div>
      </div>
    );
  };

  // Groups the currently-fetched (already filtered/paginated) tickets by
  // assigned_owner, so the manager/admin grouped view below can show each
  // person's slice without a separate fetch per person.
  const ticketsByOwner = {};
  for (const t of tickets) {
    const key = t.assigned_owner || "unassigned";
    (ticketsByOwner[key] = ticketsByOwner[key] || []).push(t);
  }

  // Newest-first, same comparator used both within an owner's section
  // (renderGroupBody) and for the flat all-owners "Group by Date" list
  // below -- category sub-grouping removed outright, 2026-11-02.
  const sortByDateDesc = (list) =>
    [...list].sort(
      (a, b) => new Date(b.received_at || b.created_date) - new Date(a.received_at || a.created_date)
    );

  const renderGroupBody = (ownerTickets) => sortByDateDesc(ownerTickets).map(renderTicketRow);

  // One collapsible "folder" in the grouped list -- a header with the
  // person's name + their own ticket count, and (when expanded) their
  // tickets. Confirmed with the user, 2026-10-28: no hierarchy any more --
  // every group here comes from who actually has tickets on the current
  // page (ticketsByOwner), not a fixed team roster, so nobody with zero
  // tickets gets an empty folder.
  const renderOwnerGroup = (ownerId, label) => {
    const ownerTickets = ticketsByOwner[ownerId] || [];
    const collapsed = collapsedOwners.has(ownerId);
    return (
      <div key={ownerId} className="inbox-group">
        <div className="inbox-group-header" onClick={() => toggleCollapsed(ownerId)}>
          <span className="inbox-sidebar-caret">
            {collapsed ? <RightOutlined /> : <DownOutlined />}
          </span>
          <span className="inbox-group-label">{label}</span>
          <span className="inbox-group-count">{ownerTickets.length}</span>
        </div>
        {!collapsed && (
          <div className="inbox-group-body">
            {ownerTickets.length === 0 ? (
              <div className="inbox-group-empty">No tickets</div>
            ) : (
              renderGroupBody(ownerTickets)
            )}
          </div>
        )}
      </div>
    );
  };

  // "All others" -- every other owner who has at least one ticket on the
  // current page, ordered by that owner's own most recent ticket (newest
  // first), not alphabetically -- confirmed with the user, 2026-10-28:
  // Unassigned/My Tickets stay pinned at the top, but an alphabetical
  // "Others" order let an older ticket under an earlier name (e.g.
  // "Deepika Singh") visually outrank a newer one under a later name
  // (e.g. "Deepti Fangon"), which reads as wrong next to each folder's
  // own newest-first ticket order.
  const ownerMostRecent = (id) =>
    Math.max(
      ...(ticketsByOwner[id] || []).map((t) => new Date(t.received_at || t.created_date).getTime() || 0)
    );
  const otherOwners = Object.keys(ticketsByOwner)
    .filter((id) => id !== "unassigned" && id !== currentEmployeeId)
    .map((id) => ({ id, name: accountOwnerOptions.find((o) => o.value === id)?.label || id }))
    .sort((a, b) => ownerMostRecent(b.id) - ownerMostRecent(a.id));

  // The same order the list currently renders in, flattened -- "Group by
  // Date" is just the one flat sort; "Group by Assign To" is Unassigned,
  // then My Tickets, then everyone else, each internally sorted, same as
  // the actual render order below. Used for the detail view's Previous/
  // Next arrows -- confirmed with the user, 2026-11-02: step through
  // whatever's currently loaded (this page, these filters), not the
  // whole unfiltered inbox or a separate fetch.
  const getOrderedTicketList = () => {
    if (groupMode === "date") return sortByDateDesc(tickets);
    return [
      ...sortByDateDesc(ticketsByOwner["unassigned"] || []),
      ...(currentEmployeeId ? sortByDateDesc(ticketsByOwner[currentEmployeeId] || []) : []),
      ...otherOwners.flatMap((o) => sortByDateDesc(ticketsByOwner[o.id] || [])),
    ];
  };

  const handleNavigateTicket = (direction) => {
    if (!selected) return;
    const ordered = getOrderedTicketList();
    const currentIndex = ordered.findIndex((t) => t.id === selected.id);
    if (currentIndex === -1) return;
    const target = ordered[currentIndex + direction];
    if (target) handleSelect(target);
  };

  const orderedForNav = selected ? getOrderedTicketList() : [];
  const selectedNavIndex = selected ? orderedForNav.findIndex((t) => t.id === selected.id) : -1;
  const hasPrevTicket = selectedNavIndex > 0;
  const hasNextTicket = selectedNavIndex !== -1 && selectedNavIndex < orderedForNav.length - 1;

  return (
    <div className="main-box">
      <Sidebar activeId={13} />
      <main className="main-content">
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
          <Header title="Inbox" />
          <div className="inbox-layout">
            <div className="inbox-list-panel">
              {needingAttention.length > 0 && (
                <div className="inbox-attention-strip" onClick={handleShowNeedingAttention}>
                  <WarningOutlined />
                  <span className="inbox-attention-title">
                    {needingAttention.length} unassigned, high-urgency ticket
                    {needingAttention.length === 1 ? "" : "s"} need{needingAttention.length === 1 ? "s" : ""}{" "}
                    attention — click to view
                  </span>
                </div>
              )}

              <div className="inbox-filters">
                <Select
                  placeholder="Status (Open + In Progress)"
                  allowClear
                  value={filters.status}
                  options={STATUS_FILTER_OPTIONS}
                  onChange={(v) => handleFilterChange("status", v)}
                />
                <Select
                  placeholder="Category"
                  allowClear
                  value={filters.category}
                  options={CATEGORY_OPTIONS}
                  onChange={(v) => handleFilterChange("category", v)}
                />
                <Select
                  placeholder="Urgency"
                  allowClear
                  value={filters.urgency}
                  options={URGENCY_OPTIONS}
                  onChange={(v) => handleFilterChange("urgency", v)}
                />
                <Select
                  placeholder="Assigned To"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={filters.assigned_owner}
                  options={[
                    { value: "unassigned", label: "Unassigned" },
                    ...filterOptions.assignable_owners.map((o) => ({ value: o.id, label: o.name })),
                  ]}
                  onChange={(v) => handleFilterChange("assigned_owner", v)}
                />
                <Select
                  placeholder="Client Name"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={filters.client_id}
                  options={filterOptions.clients.map((c) => ({ value: c.client_id, label: c.client_name }))}
                  onChange={(v) => handleFilterChange("client_id", v)}
                />
                <Select
                  placeholder="Client Location"
                  allowClear
                  showSearch
                  value={filters.client_location}
                  options={filterOptions.client_locations.map((loc) => ({ value: loc, label: loc }))}
                  onChange={(v) => handleFilterChange("client_location", v)}
                />
                <Input.Search
                  placeholder="From (sender email)"
                  allowClear
                  value={senderEmailInput}
                  onChange={(e) => setSenderEmailInput(e.target.value)}
                  onSearch={handleSenderEmailSearch}
                />
                <DatePicker.RangePicker
                  showTime={{ format: "HH:mm" }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder={["Received from", "Received to"]}
                  value={[
                    filters.date_from ? dayjs(filters.date_from) : null,
                    filters.date_to ? dayjs(filters.date_to) : null,
                  ]}
                  onChange={handleDateRangeChange}
                  style={{ gridColumn: "span 2" }}
                />
                <Input.Search
                  placeholder="Search subject, sender, client..."
                  allowClear
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onSearch={handleSearch}
                  style={{ gridColumn: "span 2" }}
                />
                <Select
                  value={filters.sort_by}
                  options={SORT_OPTIONS}
                  onChange={(v) => handleFilterChange("sort_by", v)}
                />
                <Select
                  value={groupMode}
                  options={GROUP_MODE_OPTIONS}
                  onChange={setGroupMode}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={openCompose}>
                  New
                </Button>
                <Button
                  icon={<SyncOutlined />}
                  loading={syncing}
                  disabled={syncCooldown}
                  onClick={handleManualSync}
                >
                  Refresh
                </Button>
                {(filters.status ||
                  filters.category ||
                  filters.urgency ||
                  filters.assigned_owner ||
                  filters.sender_email ||
                  filters.client_id ||
                  filters.client_location ||
                  filters.date_from ||
                  filters.date_to ||
                  filters.search) && <Button onClick={handleClearFilters}>Clear</Button>}
              </div>

              {tickets.length > 0 && (
                <div className="inbox-bulk-bar">
                  <Checkbox
                    checked={selectedIds.length === tickets.length}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < tickets.length}
                    onChange={toggleSelectAllOnPage}
                  >
                    {selectedIds.length > 0 ? `${selectedIds.length} selected` : "Select all"}
                  </Checkbox>

                  {selectedIds.length > 0 && (
                    <>
                      <Select
                        placeholder="Set status"
                        allowClear
                        value={bulkStatus}
                        options={STATUS_OPTIONS}
                        onChange={setBulkStatus}
                        style={{ minWidth: "9rem" }}
                      />
                      <Select
                        placeholder="Assign to"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        value={bulkAssignee}
                        options={accountOwnerOptions}
                        onChange={setBulkAssignee}
                        style={{ minWidth: "11rem" }}
                      />
                      <Button
                        type="primary"
                        loading={bulkApplying}
                        disabled={!bulkStatus && !bulkAssignee}
                        onClick={handleBulkApply}
                      >
                        Apply
                      </Button>
                    </>
                  )}
                </div>
              )}

              <div className="inbox-list custom-scrollbar">
                {!loading && tickets.length === 0 && (
                  <Empty
                    description="No tickets match these filters"
                    style={{ marginTop: "3rem" }}
                  />
                )}
                {groupMode === "assign_to" ? (
                  // Everyone sees every ticket, no hierarchy -- confirmed
                  // with the user, 2026-10-28. Fixed group order:
                  // Unassigned, then My Tickets, then everyone else A-Z.
                  <>
                    {renderOwnerGroup("unassigned", "Unassigned")}
                    {currentEmployeeId && renderOwnerGroup(currentEmployeeId, "My Tickets")}
                    {otherOwners.map((o) => renderOwnerGroup(o.id, o.name))}
                  </>
                ) : (
                  // "Group by Date" -- one flat newest-first list across
                  // every owner, no sections at all; each row still shows
                  // its own assignee inline. Confirmed with the user,
                  // 2026-11-02: this is the plain chronological inbox the
                  // team actually wants, not sub-divided by anything.
                  sortByDateDesc(tickets).map(renderTicketRow)
                )}
              </div>

              {totalRecords > PAGE_SIZE && (
                <div className="inbox-pagination">
                  <Pagination
                    current={currentPage}
                    total={totalRecords}
                    pageSize={PAGE_SIZE}
                    onChange={setCurrentPage}
                    showSizeChanger={false}
                    simple
                  />
                </div>
              )}
            </div>

            <div className="inbox-detail">
              {selected ? (
                <div className="inbox-detail-card">
                  {renderDetailCard()}
                </div>
              ) : (
                <Empty description="Select a ticket to view details" style={{ marginTop: "4rem" }} />
              )}
            </div>
          </div>
        </div>
      </main>

      <Modal
        open={viewModalOpen}
        onCancel={() => setViewModalOpen(false)}
        footer={null}
        width={720}
        title={null}
        destroyOnClose
      >
        {selected && <div className="inbox-detail-card">{renderDetailCard()}</div>}
      </Modal>

      <Modal
        title="Compose New Email"
        open={composeOpen}
        onCancel={() => setComposeOpen(false)}
        width={720}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setComposeOpen(false)}>
            Cancel
          </Button>,
          <Button key="send" type="primary" loading={composing} onClick={handleComposeSend}>
            Send
          </Button>,
        ]}
      >
        <div className="inbox-reply-composer">
          <div className="inbox-reply-headers">
            <div>
              <span>To</span>
              <Select
                mode="tags"
                value={composeTo}
                onChange={(values) => setComposeTo(values.filter((v) => v.includes("@")))}
                placeholder="Add a recipient email"
                tokenSeparators={[",", " "]}
                options={userDirectory}
                filterOption={(input, option) =>
                  (option?.label || "").toLowerCase().includes(input.toLowerCase())
                }
                style={{ flex: 1 }}
              />
            </div>
            <div>
              <span>Cc</span>
              <Select
                mode="tags"
                value={composeCc}
                onChange={(values) => setComposeCc(values.filter((v) => v.includes("@")))}
                placeholder="Add a Cc email (optional)"
                tokenSeparators={[",", " "]}
                options={userDirectory}
                filterOption={(input, option) =>
                  (option?.label || "").toLowerCase().includes(input.toLowerCase())
                }
                style={{ flex: 1 }}
              />
            </div>
            <div>
              <span>Bcc</span>
              <Select
                mode="tags"
                value={composeBcc}
                onChange={(values) => setComposeBcc(values.filter((v) => v.includes("@")))}
                placeholder="Add a Bcc email (optional)"
                tokenSeparators={[",", " "]}
                options={userDirectory}
                filterOption={(input, option) =>
                  (option?.label || "").toLowerCase().includes(input.toLowerCase())
                }
                style={{ flex: 1 }}
              />
            </div>
            <div>
              <span>Subject</span>
              <Input
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="Subject"
              />
            </div>
          </div>
          <div className="inbox-reply-editor" style={{ marginTop: "0.5rem" }}>
            <ReactQuill
              theme="snow"
              value={composeBody}
              onChange={setComposeBody}
              modules={REPLY_EDITOR_MODULES}
              placeholder="Write your message..."
            />
          </div>
          <Upload
            multiple
            fileList={composeAttachments.map((f, i) => ({ uid: String(i), name: f.name, status: "done" }))}
            beforeUpload={(file) => {
              setComposeAttachments((prev) => [...prev, file]);
              return false;
            }}
            onRemove={(fileItem) =>
              setComposeAttachments((prev) => prev.filter((_, i) => String(i) !== fileItem.uid))
            }
            style={{ marginTop: "0.5rem" }}
          >
            <Button icon={<UploadOutlined />}>Attach files</Button>
          </Upload>
        </div>
      </Modal>
    </div>
  );
};


export default Inbox;
